// src/anim/bodyMask.ts — Dilim ve katman türevleri (ANIMATION §4, §8; ARCHITECTURE §5.4).
//   splitClip: track kümesi ayrımı (UPPER / LOWER). three PropertyMixer aynı property'ye yazan action'ları kümülatif ağırlıkla
//     NORMALİZE eder ("override" yok) → katman = iki action'ın hiç ortak track'i olmaması.
//   sliceClip: AnimationUtils.subclip aralıkta anahtarı olmayan track'i DÜŞÜRÜR (optimize edilmiş 2 anahtarlı sabit kanallar → kemik
//     önceki action'ın pozunda asılı kalır). Kendi uygulamamız her track'i createInterpolant ile t0 ve t1'de örnekler, aradaki
//     anahtarları alır ve ZAMAN EKSENİNİ KAYDIRMAZ: duration = t1, anahtar zamanları kanonik → action.time = clipStart (kanonik sn)
//     doğrudan yazılır; clampWhenFinished t1'de durdurur (tur 2 zaman tabanı sözleşmesi).
//   poseClip: süre 0 pozlar (Death_A_Pose) mixer için 1/30 s'e uzatılır (iki anahtar, aynı değer).
//   makeAdditiveClip: Hit_A+ sırası ŞART: split(UPPER) → clone → makeClipAdditive (referans = kendi 0. karesi). makeClipAdditive hedefi
//     YERİNDE değiştirir ve blendMode = Additive atar; ters sıra (additive → split) blendMode'u Normal'e düşürüp flinch'i poz olarak
//     uyguluyordu (ANIMATION §6.1). Üç türev de blendMode'u taşır.
// Hepsi yükleme anında (ClipLibrary), sıcak yol değil.
import * as THREE from 'three';

/** Üst gövde kemikleri (three sanitize edilmiş adlar: 'handslot.l' → 'handslotl'). */
export const UPPER: ReadonlySet<string> = new Set([
  'spine', 'chest', 'head',
  'upperarml', 'lowerarml', 'wristl', 'handl', 'handslotl',
  'upperarmr', 'lowerarmr', 'wristr', 'handr', 'handslotr',
]);
/** Alt gövde kemikleri (root + hips dâhil: kök hareket / hips salınımı alt katmandan gelir). */
export const LOWER: ReadonlySet<string> = new Set([
  'root', 'hips',
  'upperlegl', 'lowerlegl', 'footl', 'toesl',
  'upperlegr', 'lowerlegr', 'footr', 'toesr',
]);

/** Poz klibi eşiği: duration ≤ 1/30 s → LoopOnce + clamp (loop anlamsız). */
export const POSE_DURATION = 1 / 30;
export const isPoseClip = (clip: THREE.AnimationClip): boolean => clip.duration <= POSE_DURATION + 1e-6;

const nodeOf = (trackName: string): string => trackName.slice(0, trackName.lastIndexOf('.'));

/** Yalnız `keep` kümesindeki kemiklerin track'leri; ad = clip.name + suffix; blendMode taşınır (69 → 39 upper / 30 lower). */
export function splitClip(clip: THREE.AnimationClip, keep: ReadonlySet<string>, suffix: '#upper' | '#lower', name: string = clip.name + suffix): THREE.AnimationClip {
  const tracks = clip.tracks.filter((t) => keep.has(nodeOf(t.name)));
  return new THREE.AnimationClip(name, clip.duration, tracks, clip.blendMode);
}

type TrackCtor = new (name: string, times: ArrayLike<number>, values: ArrayLike<number>) => THREE.KeyframeTrack;

/** [t0, t1] dilimi; zamanlar KAYDIRILMAZ (kanonik klip sn), duration = t1; hiçbir track düşmez (t0/t1'de örneklenir). */
export function sliceClip(clip: THREE.AnimationClip, t0: number, t1: number, name: string): THREE.AnimationClip {
  if (!(t1 > t0) || t0 < 0) throw new Error(`sliceClip ${clip.name}: geçersiz aralık [${t0}, ${t1}]`);
  const tracks = clip.tracks.map((src): THREE.KeyframeTrack => {
    const interp = src.createInterpolant();          // three-augment.d.ts
    const n = src.getValueSize();
    const times: number[] = [t0];
    const values: number[] = Array.from(interp.evaluate(t0).subarray(0, n));
    const st = src.times, sv = src.values;
    for (let i = 0; i < st.length; i++) {
      const t = st[i] as number;
      if (t > t0 && t < t1) {
        times.push(t);
        for (let k = 0; k < n; k++) values.push(sv[i * n + k] as number);
      }
    }
    times.push(t1);
    const end = interp.evaluate(t1);
    for (let k = 0; k < n; k++) values.push(end[k] as number);
    const out = new (src.constructor as TrackCtor)(src.name, times, values);
    out.setInterpolation(src.getInterpolation());
    return out;
  });
  return new THREE.AnimationClip(name, t1, tracks, clip.blendMode);
}

/** Süre 0 poz klibi → 1/30 s (iki anahtar aynı değer); zaten daha uzunsa kopyasız aynen döner. */
export function poseClip(clip: THREE.AnimationClip): THREE.AnimationClip {
  if (clip.duration > POSE_DURATION + 1e-6) return clip;
  const tracks = clip.tracks.map((src): THREE.KeyframeTrack => {
    const n = src.getValueSize();
    const first = Array.from(src.values.subarray(0, n));
    const out = new (src.constructor as TrackCtor)(src.name, [0, POSE_DURATION], [...first, ...first]);
    out.setInterpolation(src.getInterpolation());
    return out;
  });
  return new THREE.AnimationClip(clip.name, POSE_DURATION, tracks, clip.blendMode);
}

/** Additive türev (Hit_A+): ÖNCE üst gövde track'leri (hips deltası fizik knockback ile çakışır), SONRA clone + makeClipAdditive. */
export function makeAdditiveClip(clip: THREE.AnimationClip, name: string, keep: ReadonlySet<string> = UPPER): THREE.AnimationClip {
  const upper = splitClip(clip, keep, '#upper');
  const add = THREE.AnimationUtils.makeClipAdditive(upper.clone(), 0, upper);   // referans kare 0, fps varsayılan 30 (kare 0 için etkisiz)
  add.name = name;
  return add;
}
